import { StullChartPoint } from '@/components/stull-chart/stull-chart';

export type SelectedCalculatorState = {
  id: number;
  isReadonly: boolean;
};

export type DirtyCalculationsState = {
  [calculationId: number]: boolean;
};

export type RecipeCalculationsState = {
  stullPoints: StullChartPoint[];
  dirtyCalculations: DirtyCalculationsState;
  selectedCalculation: SelectedCalculatorState | null;
};

export type RecipeCalculationsStore = ReturnType<typeof createRecipeCalculationsStore>;

/**
 * Transient per-recipe UI state that every calculator card writes to on mount (its stull point and
 * its dirty flag) and that a handful of panels read.
 *
 * This deliberately lives outside React state. Held in a context value, every write produced a new
 * context value and re-rendered *all* consumers - including all N cards, none of which read this
 * state. N cards each writing once on load meant up to N^2 card renders, which is what made recipes
 * with 20-30 calculators crawl. As an external store, readers subscribe through
 * `useSyncExternalStore` with a selector and only re-render when their own slice changes.
 *
 * Created per `RecipeCalculationsProvider` instance, never module-level: the export-recipes page
 * mounts one provider per exported recipe at the same time.
 */
export const createRecipeCalculationsStore = () => {
  let state: RecipeCalculationsState = {
    stullPoints: [],
    dirtyCalculations: {},
    selectedCalculation: null,
  };

  const listeners = new Set<() => void>();

  const setState = (next: RecipeCalculationsState) => {
    if (next === state) {
      return;
    }
    state = next;
    listeners.forEach((listener) => listener());
  };

  return {
    subscribe: (listener: () => void) => {
      listeners.add(listener);
      return () => {
        listeners.delete(listener);
      };
    },

    getState: () => state,

    pushStullPoint: (point: StullChartPoint) =>
      setState({
        ...state,
        stullPoints: [
          ...state.stullPoints.filter((x) => x.recipeCalculationId !== point.recipeCalculationId),
          point,
        ],
      }),

    removeStullPoint: (calculationId: number) => {
      const stullPoints = state.stullPoints.filter((x) => x.recipeCalculationId !== calculationId);
      if (stullPoints.length === state.stullPoints.length) {
        return;
      }
      setState({ ...state, stullPoints });
    },

    // Merges a single card's dirty flag. The previous implementation replaced the whole map, so the
    // last card to report won and `anyDirtyCalculation` was wrong whenever several were edited.
    setCalculationDirty: (calculationId: number, isDirty: boolean) => {
      if (!!state.dirtyCalculations[calculationId] === isDirty) {
        return;
      }
      setState({
        ...state,
        dirtyCalculations: { ...state.dirtyCalculations, [calculationId]: isDirty },
      });
    },

    clearCalculationDirty: (calculationId: number) => {
      if (!(calculationId in state.dirtyCalculations)) {
        return;
      }
      const dirtyCalculations = { ...state.dirtyCalculations };
      delete dirtyCalculations[calculationId];
      setState({ ...state, dirtyCalculations });
    },

    selectCalculation: (selected: SelectedCalculatorState | null) => {
      const current = state.selectedCalculation;
      if (current?.id === selected?.id && current?.isReadonly === selected?.isReadonly) {
        return;
      }
      setState({ ...state, selectedCalculation: selected });
    },

    /**
     * Called when the opened recipe changes. Stull points are filtered rather than cleared: when a
     * tab is switched to another recipe and back, this runs *after* the restored cards have already
     * pushed their points, so clearing would leave the chart empty.
     */
    resetForRecipe: (recipeId: number) =>
      setState({
        stullPoints: state.stullPoints.filter((point) => point.recipeId === recipeId),
        dirtyCalculations: {},
        selectedCalculation: null,
      }),
  };
};
